import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useAuthStore } from '@/stores/authStore';

// Lazy load pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));

// Lazy load agent pages
const KeywordResearchPage = lazy(() => import('@/pages/KeywordResearchPage').then(m => ({ default: m.KeywordResearchPage })));
const CompetitorAnalysisPage = lazy(() => import('@/pages/CompetitorAnalysisPage').then(m => ({ default: m.CompetitorAnalysisPage })));
const CompetitorAnalysisDetailsPage = lazy(() => import('@/pages/CompetitorAnalysisDetailsPage').then(m => ({ default: m.CompetitorAnalysisDetailsPage })));
const BacklinkAnalysisPage = lazy(() => import('@/pages/BacklinkAnalysisPage').then(m => ({ default: m.BacklinkAnalysisPage })));
const ContentWriterPage = lazy(() => import('@/pages/ContentWriterPage').then(m => ({ default: m.ContentWriterPage })));
const TechnicalSEOPage = lazy(() => import('@/pages/TechnicalSEOPage').then(m => ({ default: m.TechnicalSEOPage })));
const SERPTrackerPage = lazy(() => import('@/pages/SERPTrackerPage').then(m => ({ default: m.SERPTrackerPage })));

// Lazy load Skills (agent plane v2) page
const SkillsPage = lazy(() => import('@/pages/SkillsPage').then(m => ({ default: m.SkillsPage })));
const SiteAuditPage = lazy(() => import('@/pages/SiteAuditPage').then(m => ({ default: m.SiteAuditPage })));
const SavedKeywordsPage = lazy(() => import('@/pages/SavedKeywordsPage').then(m => ({ default: m.SavedKeywordsPage })));
const PromptExplorerPage = lazy(() => import('@/pages/PromptExplorerPage').then(m => ({ default: m.PromptExplorerPage })));
const AiMcpPage = lazy(() => import('@/pages/AiMcpPage').then(m => ({ default: m.AiMcpPage })));
const RankTrackingPage = lazy(() => import('@/pages/RankTrackingPage').then(m => ({ default: m.RankTrackingPage })));

// Lazy load Chat (copilot) page
const ChatPage = lazy(() => import('@/pages/ChatPage'));

// Lazy load integrations pages
const IntegrationsPage = lazy(() => import('@/pages/Integrations'));
const OAuthCallbackPage = lazy(() => import('@/features/integrations/components').then(m => ({ default: m.OAuthCallback })));
const GoogleAdsOAuthCallbackPage = lazy(() => import('@/features/integrations/components').then(m => ({ default: m.GoogleAdsOAuthCallback })));

// Lazy load PageSpeed page
const PageSpeedPage = lazy(() => import('@/pages/PageSpeedPage'));

// Lazy load Compliance page
const CompliancePage = lazy(() => import('@/features/compliance/pages/CompliancePage').then(m => ({ default: m.CompliancePage })));

// Lazy load Cross-Domain Analysis page
const CrossDomainAnalysisPage = lazy(() => import('@/features/cross-domain/pages/CrossDomainAnalysisPage').then(m => ({ default: m.CrossDomainAnalysisPage })));

// Lazy load Company Profile pages
const CompanyProfilePage = lazy(() => import('@/features/company-profile/pages/CompanyProfilePage').then(m => ({ default: m.default })));
const CompanyProfileWizardPage = lazy(() => import('@/features/company-profile/pages/CompanyProfileWizardPage').then(m => ({ default: m.default })));
const DataSourcesManagerPage = lazy(() => import('@/features/company-profile/pages/DataSourcesManager').then(m => ({ default: m.default })));
const ProfilesListView = lazy(() => import('@/features/company-profile/pages/ProfilesListView').then(m => ({ default: m.default })));

// Lazy load GA4 pages
const GA4DashboardPage = lazy(() => import('@/features/ga4/pages').then(m => ({ default: m.GA4DashboardPage })));
const GA4OAuthCallback = lazy(() => import('@/features/ga4/components').then(m => ({ default: m.GA4OAuthCallback })));

// Lazy load AI Visibility page
const AIVisibilityPage = lazy(() => import('@/pages/AIVisibilityPage'));

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

const router = createBrowserRouter([
  // Public landing page
  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <LandingPage />
      </Suspense>
    ),
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  // Protected app routes
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'projects',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProjectsPage />
          </Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: 'integrations',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <IntegrationsPage />
          </Suspense>
        ),
      },
      {
        path: 'oauth/google/callback',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <OAuthCallbackPage />
          </Suspense>
        ),
      },
      {
        path: 'oauth/google-ads/callback',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GoogleAdsOAuthCallbackPage />
          </Suspense>
        ),
      },
      // Agent routes
      {
        path: 'keyword-research',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <KeywordResearchPage />
          </Suspense>
        ),
      },
      {
        path: 'competitor-analysis',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompetitorAnalysisPage />
          </Suspense>
        ),
      },
      {
        path: 'competitor-analysis/:id',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompetitorAnalysisDetailsPage />
          </Suspense>
        ),
      },
      {
        path: 'backlink-analysis',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <BacklinkAnalysisPage />
          </Suspense>
        ),
      },
      {
        path: 'content-writer',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ContentWriterPage />
          </Suspense>
        ),
      },
      {
        path: 'technical-seo',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <TechnicalSEOPage />
          </Suspense>
        ),
      },
      {
        path: 'serp-tracker',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SERPTrackerPage />
          </Suspense>
        ),
      },
      {
        path: 'skills',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SkillsPage />
          </Suspense>
        ),
      },
      {
        path: 'site-audit',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SiteAuditPage />
          </Suspense>
        ),
      },
      {
        path: 'saved-keywords',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <SavedKeywordsPage />
          </Suspense>
        ),
      },
      {
        path: 'prompt-explorer',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PromptExplorerPage />
          </Suspense>
        ),
      },
      {
        path: 'ai-mcp',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AiMcpPage />
          </Suspense>
        ),
      },
      {
        path: 'rank-tracking',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <RankTrackingPage />
          </Suspense>
        ),
      },
      {
        path: 'chat',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ChatPage />
          </Suspense>
        ),
      },
      {
        path: 'pagespeed',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PageSpeedPage />
          </Suspense>
        ),
      },
      {
        path: 'compliance',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompliancePage />
          </Suspense>
        ),
      },
      {
        path: 'cross-domain',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CrossDomainAnalysisPage />
          </Suspense>
        ),
      },
      // Company Profile routes
      {
        path: 'company-profile',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompanyProfilePage />
          </Suspense>
        ),
      },
      {
        path: 'company-profile/wizard',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompanyProfileWizardPage />
          </Suspense>
        ),
      },
      {
        path: 'company-profile/edit',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompanyProfileWizardPage />
          </Suspense>
        ),
      },
      {
        path: 'company-profile/data-sources',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <DataSourcesManagerPage />
          </Suspense>
        ),
      },
      {
        path: 'profiles',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProfilesListView />
          </Suspense>
        ),
      },
      // GA4 routes
      {
        path: 'ga4/dashboard',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GA4DashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'ga4/callback',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GA4OAuthCallback />
          </Suspense>
        ),
      },
      // AI Visibility route
      {
        path: 'ai-visibility',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AIVisibilityPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
